import { AfterViewInit, Directive, ElementRef, EventEmitter, Inject, Input, Optional, Output, PLATFORM_ID, Renderer2, OnDestroy } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { TranslateService } from '@ngx-translate/core';
import { timer } from 'rxjs';
import { loadLazyCssFile, loadLazyJsFile } from '../util';
import { swiperAssetsDefaultConfig, SWIPER_ASSETS } from './swiper.module';
declare const Swiper: any;


@Directive({
  selector: '[tmSwiper]',
  exportAs: 'tmSwiper',
})
export class SwiperDirective implements AfterViewInit, OnDestroy {
  /** swiper 插件初始化完成後觸發 */
  static pulginInit = new EventEmitter<void>();
  /** 全域的 swiper 插件初始化完成 */
  static isPluginInit = false;
  /** 全域的 swiper 插件在正初始化中 */
  static isPluginIniting = false;
  /** 載入插件的方法 */
  static loadPlugin(renderer: Renderer2, parentEl: Element, assetsConfig: { scripts: string[]; styles: string[]; }) {
    SwiperDirective.isPluginIniting = true;
    assetsConfig.scripts.forEach(sc => {
      const sca = sc.split('/');
      const scName = sca[sca.length - 1];
      loadLazyJsFile(renderer, scName, sc);
    });
    assetsConfig.styles.forEach(st => {
      const sta = st.split('/');
      const stName = sta[sta.length - 1];
      loadLazyCssFile(renderer, parentEl, stName, st);
    });
    checkPluginCanUse(() => {
      SwiperDirective.isPluginInit = true;
      SwiperDirective.isPluginIniting = false;
      SwiperDirective.pulginInit.emit();
      SwiperDirective.pulginInit.complete();
    });
  }
  /** 是否渲染端為瀏覽器 */ // tslint:disable-next-line: member-ordering
  protected isBrowser: boolean;
  /** 是否可以開始使用元件了 */ // tslint:disable-next-line: member-ordering
  canInit = false;
  // tslint:disable-next-line: member-ordering
  private _defultConfig: any = {};
  // tslint:disable-next-line: member-ordering
  private _config: any;
  /** swiper [config](https://swiperjs.com/swiper-api#parameters) */ // tslint:disable-next-line: member-ordering
  @Input('tmSwiper') set config(c: any) {
    if (c) {
      if ('on' in c) {
        c.on = Object.assign(this._defultConfig.on, c.on);
      }
      this._config = Object.assign(this._defultConfig, c);
    } else if (this._config) {
      this._config = this._defultConfig;
    }
  }
  get config() { return this._config; }
  /** swiper instance */ // tslint:disable-next-line: member-ordering
  instance: any;
  /** 是否已經初始化完成 */ // tslint:disable-next-line: member-ordering
  hasInited = false;
  /** 不要自動初始化 */ // tslint:disable-next-line: member-ordering
  @Input() noInit = false;
  /** Index number of currently active slide */ // tslint:disable-next-line: member-ordering
  activeIndex = 0;
  /** 目前瀏覽的每頁 slide 數量 */ // tslint:disable-next-line: member-ordering
  viewItemCount = 1;
  /** Event will be fired when currently active slide is changed, payload is activeIndex */ // tslint:disable-next-line: member-ordering
  @Output() slideChange = new EventEmitter<number>();
  /** Event will be fired on breakpoint change, payload is breakpointParams */ // tslint:disable-next-line: member-ordering
  @Output() breakpoint = new EventEmitter<any>();
  /** swiper 區塊的語意標籤 */ // tslint:disable-next-line: member-ordering
  @Input() ariaLabel = '使用Tab鍵走訪畫面上的幻燈片。';

  constructor(
    @Inject(PLATFORM_ID) private platformId: any,
    @Optional() @Inject(SWIPER_ASSETS) private assets: { scripts: string[]; styles: string[]; },
    private el: ElementRef,
    private render: Renderer2,
    private translateService: TranslateService,
  ) {
    this.isBrowser = isPlatformBrowser(this.platformId);
    this.translateService.get('swiper').subscribe(swiperi18n => {
      Object.assign(this._defultConfig, {
        a11y: {enabled: false, ...swiperi18n},
        watchSlidesProgress: true,
        keyboard: {
          enabled: true,
          onlyInViewport: false,
          pageUpDown: true,
        },
        on: {
          init: (swiper) => {
            this.hasInited = true;
          },
        }
      });
    });
  }

  ngAfterViewInit() {
    if (this.isBrowser) {
      if (!SwiperDirective.isPluginInit && !SwiperDirective.isPluginIniting) {
        if (isPluginLoaded()) {
          SwiperDirective.isPluginInit = true;
        } else {
          SwiperDirective.loadPlugin(this.render, this.el.nativeElement, this.assets || swiperAssetsDefaultConfig);
        }
      }

      if (!SwiperDirective.isPluginInit) {
        SwiperDirective.pulginInit.subscribe(() => {
          this.canInit = true;
          if (!this.noInit) {
            this.init();
          }
        });
      } else {
        this.canInit = true;
        if (!this.noInit) {
          this.init();
        }
      }
    }
  }

  ngOnDestroy(): void {
    this.destroy();
  }

  init() {
    this.instance = new Swiper(this.el.nativeElement, this.config);
    this.render.setAttribute(this.el.nativeElement, 'aria-label', `${this.ariaLabel}共${this.instance.slides.length}筆資料`);
    (this.el.nativeElement as HTMLElement).tabIndex = -1;
    for (let i = 0; i < this.instance.slides.length; i++) {
      const slideEl = this.instance.slides[i] as HTMLElement;
      slideEl.setAttribute('role', 'group');
      slideEl.tabIndex = -1;
      slideEl.setAttribute('aria-label', `第${i + 1}筆`);
    }

    this.calViewItemCount(this.config);
    this.instance.on('slideChange', this.onSlideChange);
    this.instance.on('breakpoint', this.onBreakpoint);
    this.instance.$el.on('focus', this.onFocus, true);
  }

  destroy() {
    if (this.instance) {
      this.instance.off('slideChange', this.onSlideChange);
      this.instance.off('breakpoint', this.onBreakpoint);
      this.instance.$el.off('focus', this.onFocus, true);
      this.instance.destroy(true, true);
      delete this.instance;
      this.hasInited = false;
      this.activeIndex = 0;
      this.viewItemCount = 1;
      this.render.setAttribute(this.el.nativeElement, 'aria-label', ``);
    }
  }

  private onSlideChange = (swiper) => {
    this.activeIndex = swiper.activeIndex;
    this.slideChange.emit(swiper.activeIndex);
  }

  private onBreakpoint = (swiper, breakpointParams) => {
    this.calViewItemCount(this.config);
    this.activeIndex = swiper.activeIndex;
    this.breakpoint.emit(this.viewItemCount);
  }

  private onFocus = (e) => {
    const slideEl = e.target.closest(`.${this.instance.params.slideClass}`);
    if (!slideEl || !this.instance.slides.includes(slideEl)) { return; }
    const isActive = this.instance.slides.indexOf(slideEl) === this.instance.activeIndex;
    const isVisible =
      this.instance.params.watchSlidesProgress &&
      this.instance.visibleSlides &&
      this.instance.visibleSlides.includes(slideEl);
    if (isActive || isVisible) { return; }
    this.instance.slideTo(this.instance.slides.indexOf(slideEl), 0);
  }

  private calViewItemCount(config: any) {
    if (config) {
      if (this.config.breakpoints) {
        const keys = Object.keys(config.breakpoints);
        keys.sort((a, b) => +a - +b);
        let matchBreakpoint = false;
        for (let i = keys.length - 1; i >= 0 && !matchBreakpoint; i--) {
          const breakpoint = +keys[i];
          if (window.innerWidth >= breakpoint && config.breakpoints[breakpoint].slidesPerView) {
            matchBreakpoint = true;
            if (typeof config.breakpoints[breakpoint].slidesPerView === 'number') {
              this.viewItemCount = config.breakpoints[breakpoint].slidesPerView;
            } else {
              const viewItemCount = this.instance.params.watchSlidesProgress && this.instance.visibleSlides.length;
              if (viewItemCount !== this.viewItemCount ) {
                this.viewItemCount = viewItemCount;
              }
            }
          }
        }
        if (!matchBreakpoint) {
          this.viewItemCount = config.slidesPerView || 1;
        }
      } else {
        this.viewItemCount = config.slidesPerView || 1;
      }
    }
  }

  update() {
    if (this.instance) {
      this.instance.update();
    }
  }

  slideNext() {
    this.instance.slideNext();
  }

  slidePrev() {
    this.instance.slidePrev();
  }

  changeLanguageDirection(direction: 'rtl' | 'ltr') {
    this.instance.changeLanguageDirection(direction);
  }
}

function isPluginLoaded() {
  return window['Swiper'];
}

const checkPluginCanUse = (cb: () => void) => {
  const cpTmSubc = timer(0, 150).subscribe(t => {
    if (isPluginLoaded()) {
      cpTmSubc.unsubscribe();
      cb();
    }
  });
};
